import { Menu, MenuButton, MenuItem, MenuItems, Transition } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import React, { Fragment, useCallback, } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser, faArrowRightFromBracket } from "@fortawesome/pro-light-svg-icons"
import OrganisationLogo from "./components/organisation-logo";
import Sidebar from "./components/sidebar";
import ApplicationMenu from "./components/applications-menu";
import useAuthenticationContext from "../../services/context/authentication/use-authentication-context.ts";
import GoogleAnalytics from "@/services/context/google-analytics/google-analytics.tsx";

interface LayoutPageProps extends React.PropsWithChildren {
    menuRightItem?: React.ReactNode;
}

const LayoutPage: React.FC<LayoutPageProps> = ({ menuRightItem, children }) => {
    const { authenticationResponse, resetAuthenticationContext } = useAuthenticationContext();

    const navigate = useNavigate();

    const handleDisconnect = useCallback(() => {
        resetAuthenticationContext()
        navigate('/sign-in')
    }, [resetAuthenticationContext, navigate])

    return (
        <>
            <GoogleAnalytics />

            <header className='bg-white z-2'>
                <nav className="mx-auto flex items-center justify-between py-1 px-2">

                    <ApplicationMenu />

                    {menuRightItem}

                    <div className='ml-auto flex'>
                        <OrganisationLogo id={authenticationResponse?.user?.organizationId} />

                        <Menu as="div" className="relative hover:bg-gray-50 rounded-lg pl-2 pr-4 py-1.5">
                            <MenuButton className="-m-1.5 flex items-center p-1.5 focus:outline-none">
                                <span className="sr-only">
                                    Open user menu
                                </span>

                                <div className="hidden lg:flex lg:items-center">
                                    <span
                                        className="ml-4 text-sm font-semibold leading-6 text-gray-900"
                                        aria-hidden="true">
                                        {authenticationResponse?.user?.firstName}
                                    </span>
                                    <ChevronDownIcon
                                        className="w-5 h-5 ml-2"
                                        aria-hidden="true" />
                                </div>
                            </MenuButton>
                            <Transition
                                as={Fragment}
                                enter="transition ease-out duration-100"
                                enterFrom="transform opacity-0 scale-95"
                                enterTo="transform opacity-100 scale-100"
                                leave="transition ease-in duration-75"
                                leaveFrom="transform opacity-100 scale-100"
                                leaveTo="transform opacity-0 scale-95"
                            >
                                <MenuItems className="flex flex-col gap-y-1 p-1.5 absolute text-sm w-50 right-0 z-10 mt-2.5 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-gray-900/5 focus:outline-hidden">

                                    <MenuItem>
                                        <a href={`https://am.treegram.fr/User/Details?userId=${authenticationResponse?.user.id}`} className="block px-2 py-1 leading-6 text-gray-900 cursor-pointer hover:bg-gray-100 focus:outline-none focus:bg-gray-100 transition duration-150 ease-in-out rounded-md">
                                            <FontAwesomeIcon icon={faUser} className="mr-2" fixedWidth />
                                            Gestion du compte
                                        </a>
                                    </MenuItem>

                                    <MenuItem>
                                        <div className="border-t border-solid"></div>
                                    </MenuItem>

                                    <MenuItem>
                                        <button className="flex flex-row items-center px-2 py-1 leading-6 text-gray-900 cursor-pointer hover:bg-gray-100 focus:outline-none focus:bg-gray-100 transition duration-150 ease-in-out rounded-md"
                                            onClick={handleDisconnect}>
                                            <FontAwesomeIcon icon={faArrowRightFromBracket} fixedWidth className="mr-2" />
                                            Déconnexion
                                        </button>
                                    </MenuItem>
                                </MenuItems>
                            </Transition>
                        </Menu>
                    </div>
                </nav>
            </header>

            <div className="flex-1 flex flex-row h-full overflow-hidden">
                <Sidebar />
                <main role="main" id="main-content" className="flex-1 overflow-y-auto overflow-x-hidden">
                    {children}
                </main>
            </div>
        </>
    );
};

export default LayoutPage;